import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  Chip,
  List,
  ListSubheader,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '@/contexts/AuthContext';
import { ROLE_LABELS } from '@/lib/permissions';
import { ROLE_COLORS } from './DashboardHeader';

export interface DrawerNavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** Nama kelompok menu; item berurutan dengan group sama tampil di bawah satu judul. */
  group: string;
  badge?: number;
}

interface NavigationDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Seluruh menu yang boleh diakses (sudah difilter permission). */
  items: DrawerNavItem[];
  activeKey?: string;
  onSelect: (key: string) => void;
}

/**
 * Sidebar navigasi bergaya m-banking: header berwarna sesuai role dengan
 * identitas pengguna, lalu seluruh menu dikelompokkan per kategori.
 * Dibuka lewat tombol burger di DashboardHeader.
 */
const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
  open,
  onClose,
  items,
  activeKey,
  onSelect,
}) => {
  const { user } = useAuth();
  if (!user) return null;

  const roleColor = ROLE_COLORS[user.role] ?? '#1976d2';
  const roleLabel = ROLE_LABELS[user.role] ?? user.role;
  const initials = user.fullName
    ? user.fullName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : user.email.slice(0, 2).toUpperCase();

  // Kelompokkan item berdasarkan urutan kemunculan group.
  const groups: Array<{ name: string; items: DrawerNavItem[] }> = [];
  items.forEach((item) => {
    const last = groups[groups.length - 1];
    if (last && last.name === item.group) {
      last.items.push(item);
    } else {
      const existing = groups.find((g) => g.name === item.group);
      if (existing) existing.items.push(item);
      else groups.push({ name: item.group, items: [item] });
    }
  });

  const handleSelect = (key: string) => {
    onSelect(key);
    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      PaperProps={{
        sx: {
          width: 296,
          borderTopRightRadius: 20,
          borderBottomRightRadius: 20,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header identitas — gaya m-banking */}
      <Box
        sx={{
          px: 2.5,
          pt: 3.5,
          pb: 2.5,
          color: 'white',
          background: `linear-gradient(135deg, ${roleColor} 0%, ${alpha(roleColor, 0.75)} 100%)`,
        }}
      >
        <Avatar
          sx={{
            width: 52,
            height: 52,
            mb: 1.25,
            fontWeight: 700,
            bgcolor: 'rgba(255, 255, 255, 0.25)',
            color: 'white',
            border: '2px solid rgba(255, 255, 255, 0.5)',
          }}
        >
          {initials}
        </Avatar>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {user.fullName || user.email}
        </Typography>
        <Chip
          label={roleLabel}
          size="small"
          sx={{
            mt: 0.75,
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 600,
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
          }}
        />
      </Box>

      {/* Menu per kelompok */}
      <Box sx={{ overflowY: 'auto', flex: 1, pb: 2 }}>
        {groups.map((group) => (
          <List
            key={group.name}
            dense
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  lineHeight: 2.5,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  bgcolor: 'transparent',
                }}
              >
                {group.name}
              </ListSubheader>
            }
            sx={{ px: 1.25, pt: 1 }}
          >
            {group.items.map((item) => {
              const isActive = item.key === activeKey;
              return (
                <ListItemButton
                  key={item.key}
                  selected={isActive}
                  onClick={() => handleSelect(item.key)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.25,
                    '&.Mui-selected': {
                      bgcolor: alpha(roleColor, 0.12),
                      color: roleColor,
                      '&:hover': { bgcolor: alpha(roleColor, 0.18) },
                      '& .MuiListItemIcon-root': { color: roleColor },
                      '& .MuiListItemText-primary': { fontWeight: 700 },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: 'text.secondary' }}>
                    {item.badge && item.badge > 0 ? (
                      <Badge badgeContent={item.badge} color="error" max={99}>
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ))}
      </Box>
    </Drawer>
  );
};

export default NavigationDrawer;
